/**
 * Cognito Auth Wrapper
 * Built on amazon-cognito-identity-js for SRP + MFA flows.
 *
 * This module wraps the raw Cognito SDK to provide:
 *   - Promise-based login (handles NEW_PASSWORD_REQUIRED + MFA challenges)
 *   - TOTP enrollment flow (associate + verify)
 *   - Session retrieval + token refresh
 *   - Logout
 *
 * Claims expected on the JWT (injected by the pre-token-generation Lambda, M1.4):
 *   custom:adminId, custom:adminRole, custom:status
 *
 * The auth context (`auth-context.tsx`) consumes this module and exposes React-friendly state.
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  ICognitoUserAttributeData,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { config } from './config';

const userPool = new CognitoUserPool({
  UserPoolId: config.cognito.userPoolId,
  ClientId: config.cognito.clientId,
});

export type AdminRole = 'superadmin' | 'admin' | 'support-lead' | 'csr' | 'read-only';

export type AuthClaims = {
  adminId: string;
  email: string;
  role: AdminRole;
  status: 'active' | 'suspended' | 'pending-mfa-enrollment';
};

export type LoginChallenge =
  | { kind: 'none' }
  | { kind: 'new-password-required'; user: CognitoUser; userAttributes: Record<string, string> }
  | { kind: 'mfa-setup'; user: CognitoUser; secretCode: string }
  | { kind: 'mfa-challenge'; user: CognitoUser };

export type LoginResult =
  | { kind: 'success'; session: CognitoUserSession; claims: AuthClaims }
  | LoginChallenge;

/**
 * Initiate login. Returns either a success (session + claims) or a challenge the UI must resolve.
 */
export function login(email: string, password: string): Promise<LoginResult> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    user.authenticateUser(authDetails, {
      onSuccess: (session) => {
        resolve({ kind: 'success', session, claims: extractClaims(session) });
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes) => {
        resolve({ kind: 'new-password-required', user, userAttributes });
      },
      mfaSetup: () => {
        // Start TOTP enrollment — pool is configured SOFTWARE_TOKEN_MFA only
        user.associateSoftwareToken({
          associateSecretCode: (secretCode) => {
            resolve({ kind: 'mfa-setup', user, secretCode });
          },
          onFailure: (err) => reject(err),
        });
      },
      totpRequired: () => {
        resolve({ kind: 'mfa-challenge', user });
      },
      selectMFAType: () => {
        // Pool has only SOFTWARE_TOKEN_MFA enabled; should never hit this
        reject(new Error('unexpected MFA selection challenge — pool config issue'));
      },
      mfaRequired: () => {
        // SMS MFA — disabled on admin pool. Should never hit.
        reject(new Error('SMS MFA is disabled on admin pool'));
      },
    });
  });
}

/**
 * Complete NEW_PASSWORD_REQUIRED challenge with a user-chosen password.
 */
export function completeNewPassword(
  user: CognitoUser,
  newPassword: string,
  requiredAttributes: Record<string, string>,
): Promise<LoginResult> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, requiredAttributes, {
      onSuccess: (session) => resolve({ kind: 'success', session, claims: extractClaims(session) }),
      onFailure: (err) => reject(err),
      mfaSetup: () => {
        user.associateSoftwareToken({
          associateSecretCode: (secretCode) => resolve({ kind: 'mfa-setup', user, secretCode }),
          onFailure: (err) => reject(err),
        });
      },
      totpRequired: () => resolve({ kind: 'mfa-challenge', user }),
    });
  });
}

/**
 * Verify the TOTP code during initial enrollment.
 */
export function verifyTotpEnrollment(user: CognitoUser, totpCode: string): Promise<void> {
  return new Promise((resolve, reject) => {
    user.verifySoftwareToken(totpCode, 'AdminDashboard', {
      onSuccess: () => {
        user.setUserMfaPreference(
          null,
          { Enabled: true, PreferredMfa: true },
          (err) => (err ? reject(err) : resolve()),
        );
      },
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Submit the TOTP code in response to a login MFA challenge.
 */
export function submitTotpChallenge(user: CognitoUser, totpCode: string): Promise<LoginResult> {
  return new Promise((resolve, reject) => {
    user.sendMFACode(
      totpCode,
      {
        onSuccess: (session) => resolve({ kind: 'success', session, claims: extractClaims(session) }),
        onFailure: (err) => reject(err),
      },
      'SOFTWARE_TOKEN_MFA',
    );
  });
}

/**
 * Retrieve the current session (if any). Returns null when no active session.
 * Automatically refreshes an expired access token using the refresh token.
 */
export function getCurrentSession(): Promise<{ session: CognitoUserSession; claims: AuthClaims } | null> {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser();
    if (!user) return resolve(null);

    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve({ session, claims: extractClaims(session) });
    });
  });
}

/**
 * Sign out + clear local tokens. Also signals a global sign-out (revokes refresh tokens).
 */
export function logout(): Promise<void> {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser();
    if (!user) return resolve();
    user.globalSignOut({
      onSuccess: () => resolve(),
      onFailure: () => resolve(), // best-effort; clear local state regardless
    });
    user.signOut();
  });
}

/**
 * Extract admin claims from an id-token payload.
 */
function extractClaims(session: CognitoUserSession): AuthClaims {
  const payload = session.getIdToken().payload as Record<string, unknown>;
  const adminId = (payload['custom:adminId'] ?? payload.sub) as string | undefined;
  const email = payload.email as string | undefined;
  const role = payload['custom:adminRole'] as AdminRole | undefined;
  const status = (payload['custom:status'] ?? 'active') as AuthClaims['status'];

  if (!adminId) throw new Error('JWT missing custom:adminId claim');
  if (!email) throw new Error('JWT missing email claim');
  if (!role) throw new Error('JWT missing custom:adminRole claim');

  return { adminId, email, role, status };
}

/**
 * Return the bearer token to attach to admin API requests.
 *
 * IMPORTANT — returns the **ID token**, not the access token. The admin Lambdas
 * depend on claims only present in ID tokens (`email`, `custom:adminId`,
 * `custom:adminRole`, `custom:status`). Access tokens carry only `sub`, `client_id`,
 * `token_use`, etc. and would fail extractAdminContext() with "missing email claim".
 *
 * API Gateway's JWT authorizer accepts either token (validates `aud` on ID tokens,
 * `client_id` on access tokens) — so using the ID token as bearer is the
 * AWS-sanctioned pattern for Cognito + API Gateway when the backend needs rich claims.
 *
 * Function name retained for caller compatibility; see api-client.ts.
 */
export function getAccessToken(): Promise<string | null> {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export type { CognitoUser };

// Silence unused-export warning for rarely-used types
export type _Unused = ICognitoUserAttributeData | CognitoUserAttribute;
